import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  ShoppingCart, 
  Package, 
  Receipt, 
  Plus, 
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  MessageSquare,
  Warehouse
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const P2PDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pr: { open: 0, approved: 0, ordered: 0, total: 0 },
    rfq: { open: 0, in_progress: 0, closed: 0, total: 0 },
    po: { open: 0, received: 0, invoiced: 0, total: 0 },
    gr: { posted: 0, total: 0 },
    invoice: { parked: 0, posted: 0, paid: 0, total: 0 }
  });
  const [recentDocs, setRecentDocs] = useState({
    prs: [],
    rfqs: [],
    pos: [],
    grs: [],
    invoices: []
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load all document counts in parallel
      const [prRes, rfqRes, poRes, grRes, invRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/purchase-requisitions`),
        axios.get(`${BACKEND_URL}/api/rfqs`),
        axios.get(`${BACKEND_URL}/api/purchase-orders`),
        axios.get(`${BACKEND_URL}/api/goods-receipts`),
        axios.get(`${BACKEND_URL}/api/invoices`)
      ]);

      // Calculate PR stats
      const prs = prRes.data;
      const prStats = {
        open: prs.filter(p => p.status === 'OPEN').length,
        approved: prs.filter(p => p.status === 'APPROVED').length,
        ordered: prs.filter(p => ['PARTIALLY_ORDERED', 'FULLY_ORDERED'].includes(p.status)).length,
        total: prs.length
      };

      // Calculate RFQ stats
      const rfqs = rfqRes.data;
      const rfqStats = {
        open: rfqs.filter(r => r.status === 'OPEN').length,
        in_progress: rfqs.filter(r => r.status === 'IN_PROGRESS').length,
        closed: rfqs.filter(r => r.status === 'CLOSED').length,
        total: rfqs.length
      };

      // Calculate PO stats
      const pos = poRes.data;
      const poStats = {
        open: pos.filter(p => p.status === 'OPEN').length,
        received: pos.filter(p => ['PARTIALLY_RECEIVED', 'FULLY_RECEIVED'].includes(p.status)).length,
        invoiced: pos.filter(p => ['PARTIALLY_INVOICED', 'FULLY_INVOICED'].includes(p.status)).length,
        total: pos.length
      };

      // Calculate GR stats
      const grs = grRes.data;
      const grStats = {
        posted: grs.filter(g => g.status === 'POSTED').length,
        total: grs.length
      };

      // Calculate Invoice stats
      const invoices = invRes.data;
      const invStats = {
        parked: invoices.filter(i => i.status === 'PARKED').length,
        posted: invoices.filter(i => i.status === 'POSTED').length,
        paid: invoices.filter(i => i.status === 'PAID').length,
        total: invoices.length
      };

      setStats({
        pr: prStats,
        rfq: rfqStats,
        po: poStats,
        gr: grStats,
        invoice: invStats
      });

      // Get recent documents (last 5)
      setRecentDocs({
        prs: prs.slice(0, 5),
        rfqs: rfqs.slice(0, 5),
        pos: pos.slice(0, 5),
        grs: grs.slice(0, 5),
        invoices: invoices.slice(0, 5)
      });

    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'OPEN': 'bg-orange-100 text-orange-800',
      'APPROVED': 'bg-green-100 text-green-800',
      'PARTIALLY_ORDERED': 'bg-yellow-100 text-yellow-800',
      'FULLY_ORDERED': 'bg-green-100 text-green-800',
      'REJECTED': 'bg-red-100 text-red-800',
      'PARTIALLY_RECEIVED': 'bg-yellow-100 text-yellow-800',
      'FULLY_RECEIVED': 'bg-green-100 text-green-800',
      'PARTIALLY_INVOICED': 'bg-yellow-100 text-yellow-800',
      'FULLY_INVOICED': 'bg-green-100 text-green-800',
      'POSTED': 'bg-green-100 text-green-800',
      'PARKED': 'bg-gray-100 text-gray-800',
      'PAID': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const StatCard = ({ title, icon: Icon, color, stats }) => (
    <div className={`${color} rounded-lg p-6 shadow-sm border`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color.replace('bg-', 'bg-opacity-20 bg-')}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold">{stats.total}</p>
          <p className="text-sm opacity-80">Total {title}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-opacity-20 border-current">
        {Object.entries(stats).map(([key, value]) => {
          if (key === 'total') return null;
          return (
            <div key={key} className="text-center">
              <p className="text-lg font-semibold">{value}</p>
              <p className="text-xs opacity-70 capitalize">{key}</p>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading P2P Dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Procure-to-Pay Dashboard</h1>
              <p className="text-gray-600 mt-1">Overview of all P2P transactions</p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => navigate('/p2p/pr-create')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create PR
              </Button>
              <Button 
                onClick={() => navigate('/p2p/rfq-create')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create RFQ
              </Button>
              <Button 
                onClick={() => navigate('/p2p/po-create')}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create PO
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <StatCard
            title="Purchase Requisitions"
            icon={FileText}
            color="bg-orange-50 text-orange-900"
            stats={stats.pr}
          />
          <StatCard
            title="RFQs"
            icon={MessageSquare}
            color="bg-orange-50 text-orange-900"
            stats={stats.rfq}
          />
          <StatCard
            title="Purchase Orders"
            icon={ShoppingCart}
            color="bg-green-50 text-green-900"
            stats={stats.po}
          />
          <StatCard
            title="Goods Receipts"
            icon={Package}
            color="bg-orange-50 text-orange-900"
            stats={stats.gr}
          />
          <StatCard
            title="Invoices"
            icon={Receipt}
            color="bg-orange-50 text-orange-900"
            stats={stats.invoice}
          />
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2"
              onClick={() => navigate('/p2p/pr-list')}
            >
              <FileText className="w-6 h-6 text-orange-600" />
              <span>View PRs</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2"
              onClick={() => navigate('/p2p/rfq-list')}
            >
              <MessageSquare className="w-6 h-6 text-orange-600" />
              <span>View RFQs</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2"
              onClick={() => navigate('/p2p/po-list')}
            >
              <ShoppingCart className="w-6 h-6 text-green-600" />
              <span>View POs</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2"
              onClick={() => navigate('/p2p/gr-list')}
            >
              <Package className="w-6 h-6 text-orange-600" />
              <span>View GRs</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2"
              onClick={() => navigate('/p2p/ir-list')}
            >
              <Receipt className="w-6 h-6 text-orange-600" />
              <span>View Invoices</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2"
              onClick={() => navigate('/p2p/gr-create')}
            >
              <Package className="w-6 h-6 text-orange-600" />
              <span>Post GR</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2"
              onClick={() => navigate('/p2p/ir-create')}
            >
              <Receipt className="w-6 h-6 text-orange-600" />
              <span>Enter Invoice</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2 border-pink-200 hover:bg-pink-50"
              onClick={() => navigate('/p2p/ses-list')}
            >
              <Clock className="w-6 h-6 text-pink-600" />
              <span>Service Entries</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2 border-pink-200 hover:bg-pink-50"
              onClick={() => navigate('/p2p/ses-create')}
            >
              <Plus className="w-6 h-6 text-pink-600" />
              <span>Create SES</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2 border-orange-200 hover:bg-orange-50"
              onClick={() => navigate('/inventory')}
            >
              <Warehouse className="w-6 h-6 text-orange-600" />
              <span>Inventory Mgmt</span>
            </Button>
          </div>
        </div>

        {/* Recent Documents */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent PRs */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-600" />
                Recent Purchase Requisitions
              </h3>
            </div>
            <div className="divide-y">
              {recentDocs.prs.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No purchase requisitions yet</p>
                </div>
              ) : (
                recentDocs.prs.map((pr) => (
                  <div
                    key={pr.pr_number}
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/p2p/pr/${pr.pr_number}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-orange-600">{pr.pr_number}</p>
                        <p className="text-sm text-gray-600">{pr.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Created by {pr.created_by} • {pr.items.length} items
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${getStatusColor(pr.status)}`}>
                        {pr.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent POs */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-green-600" />
                Recent Purchase Orders
              </h3>
            </div>
            <div className="divide-y">
              {recentDocs.pos.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No purchase orders yet</p>
                </div>
              ) : (
                recentDocs.pos.map((po) => (
                  <div
                    key={po.po_number}
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/p2p/po/${po.po_number}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-green-600">{po.po_number}</p>
                        <p className="text-sm text-gray-600">
                          Vendor: {po.vendor_id} • ${po.total_value.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {po.items.length} items
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${getStatusColor(po.status)}`}>
                        {po.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default P2PDashboard;
